import type { SxProps, Theme } from '@mui/material'
import { getFieldSurfaceStyle, isOutlineLabelLayout } from './fieldSurfaces/registry'
import type { FieldSurface } from './fieldSurfaces/types'
import { FORM_KIOT_STYLE } from './formFieldTokens'

export const KIOT_FORM_FIELD_CLASS = 'kiot-form-field'

/** Gộp `sx` thành object/function — tránh mảng (MUI propTypes chỉ chấp nhận object | function). */
export function mergeSx(...parts: (SxProps<Theme> | undefined | false)[]): SxProps<Theme> {
  const filtered = parts.filter(Boolean) as Exclude<SxProps<Theme>, undefined>[]
  if (filtered.length === 0) return {}
  if (filtered.length === 1) return filtered[0]
  return (theme: Theme) => {
    let result: Record<string, unknown> = {}
    for (const part of filtered) {
      const resolved = typeof part === 'function' ? part(theme) : part
      if (resolved && typeof resolved === 'object') {
        result = { ...result, ...(resolved as Record<string, unknown>) }
      }
    }
    return result
  }
}

const kForm = FORM_KIOT_STYLE

const buildKiotInputFont = (surface: FieldSurface) => {
  const k = getFieldSurfaceStyle(surface)
  return {
    fontFamily: `${k.fontFamily} !important`,
    fontSize: `${k.inputFontSizePx}px !important`,
    fontWeight: k.inputFontWeight,
    lineHeight: `${k.lineHeight} !important`,
  } as const
}

/** Ghi đè MUI FormLabel / InputBase (theme `text.secondary` làm chữ mờ). */
export function getFormFieldKiotScopeSx(surface: FieldSurface = 'form'): SxProps<Theme> {
  const k = getFieldSurfaceStyle(surface)
  return (theme) => ({
    [`&.${KIOT_FORM_FIELD_CLASS}`]: {
      '& .MuiFormLabel-root': {
        color: `${k.labelColor} !important`,
        opacity: '1 !important',
        fontFamily: `${k.fontFamily} !important`,
        fontSize: `${k.labelFontSizePx}px !important`,
        fontWeight: k.labelFontWeight,
        lineHeight: k.lineHeight,
        '& .MuiFormLabel-asterisk, & .MuiInputLabel-asterisk': {
          color: `${theme.palette.error.main} !important`,
        },
      },
      '& .MuiInputBase-root': {
        color: `${k.inputColor} !important`,
        opacity: '1 !important',
        fontFamily: k.fontFamily,
      },
      '& .MuiInputBase-input': {
        opacity: '1 !important',
      },
    },
  })
}

/** @deprecated form surface — dùng `getFormFieldKiotScopeSx('form')`. */
export const formFieldKiotScopeSx: SxProps<Theme> = getFormFieldKiotScopeSx('form')

/** Nhãn phía trên ô nhập (layout `above`). */
export function getFormFieldAboveLabelSx(surface: FieldSurface = 'form'): SxProps<Theme> {
  const k = getFieldSurfaceStyle(surface)
  return (theme) => ({
    display: 'block',
    position: 'static',
    transform: 'none',
    fontFamily: `${k.fontFamily} !important`,
    fontSize: `${k.labelFontSizePx}px !important`,
    fontWeight: `${k.labelFontWeight} !important`,
    lineHeight: 1.2,
    color: `${k.labelColor} !important`,
    opacity: '1 !important',
    mb: 0,
    pb: 0,
    '&.Mui-focused': { color: `${k.labelColor} !important` },
    '&.Mui-error': { color: `${theme.palette.error.main} !important` },
    '&.Mui-disabled': { color: `${k.textMuted} !important` },
    '& .MuiFormLabel-asterisk': {
      color: `${theme.palette.error.main} !important`,
    },
  })
}

export const formFieldAboveLabelSx: SxProps<Theme> = getFormFieldAboveLabelSx('form')

/** Hàng nhãn + link phụ (vd. "Tạo mới") cùng dòng. */
export function getFormFieldLabelRowSx(surface: FieldSurface = 'form'): SxProps<Theme> {
  const k = getFieldSurfaceStyle(surface)
  return {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 1,
    mb: `${k.labelGapPx}px`,
    lineHeight: 1.2,
    minHeight: 'unset',
  }
}

export const formFieldLabelRowSx: SxProps<Theme> = getFormFieldLabelRowSx('form')

/** Link hành động cạnh nhãn (Tạo mới, Chọn…). */
export function getFormFieldLabelActionSx(surface: FieldSurface = 'form'): SxProps<Theme> {
  const k = getFieldSurfaceStyle(surface)
  return {
  fontFamily: k.fontFamily,
  fontSize: `${k.labelFontSizePx}px`,
  fontWeight: k.labelFontWeight,
  lineHeight: k.lineHeight,
  color: k.focusColor,
  cursor: 'pointer',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  border: 'none',
  background: 'none',
  padding: 0,
  '&:hover': { textDecoration: 'underline' },
  }
}

export const formFieldLabelActionSx: SxProps<Theme> = getFormFieldLabelActionSx('form')

/** Outlined input / select. */
export function getFormFieldKiotOutlinedInputSx(surface: FieldSurface = 'form'): SxProps<Theme> {
  const k = getFieldSurfaceStyle(surface)
  const kiotInputFont = buildKiotInputFont(surface)
  const underline = (k.inputChrome ?? 'box') === 'underline'
  const borderWidth = underline ? '0 0 1px 0' : '1px'
  const focusBorderWidth = underline ? '0 0 2px 0' : '1px'
  return (theme) => ({
  fontFamily: k.fontFamily,
  '& .MuiOutlinedInput-root': {
    borderRadius: `${k.borderRadiusPx}px !important`,
    bgcolor: `${k.inputBg} !important`,
    fontFamily: k.fontFamily,
    fontSize: `${k.inputFontSizePx}px`,
    fontWeight: k.inputFontWeight,
    minHeight: `${k.inputHeightPx}px !important`,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    ...(underline
      ? {
          px: '0 !important',
          boxShadow: 'none !important',
        }
      : {}),
    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
      borderRadius: `${k.borderRadiusPx}px !important`,
      borderColor: `${k.borderColor} !important`,
      borderWidth: `${borderWidth} !important`,
      ...(underline
        ? {
            borderStyle: 'solid !important',
            legend: { display: 'none' },
          }
        : {}),
    },
    '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${k.borderHover} !important`,
      ...(underline ? { borderWidth: `${borderWidth} !important` } : {}),
    },
    '&.Mui-focused': {
      boxShadow: `${k.focusShadow} !important`,
    },
    '&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${k.focusColor} !important`,
      borderWidth: `${focusBorderWidth} !important`,
    },
    /* Error — hex từ theme + !important ('error.main !important' không resolve palette). */
    '&.Mui-error fieldset, &.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.error.main} !important`,
      ...(underline ? { borderWidth: `${borderWidth} !important` } : {}),
    },
    '&.Mui-error:hover fieldset, &.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.error.dark} !important`,
    },
    '&.Mui-error.Mui-focused': {
      boxShadow: underline ? 'none !important' : `0 0 0 2px ${theme.palette.error.main}38 !important`,
    },
    '&.Mui-error.Mui-focused fieldset, &.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.error.main} !important`,
      borderWidth: `${focusBorderWidth} !important`,
    },
    '&.Mui-disabled': {
      bgcolor: `${k.inputBgDisabled} !important`,
    },
  },
  '& .MuiInputBase-root': {
    ...kiotInputFont,
    color: `${k.inputColor} !important`,
    bgcolor: k.inputBg,
    opacity: '1 !important',
  },
  '& .MuiInputBase-input': {
    ...kiotInputFont,
    color: `${k.inputColor} !important`,
    py: underline ? '6px !important' : '8px !important',
    px: `${k.inputPaddingX}px !important`,
    boxSizing: 'border-box',
    opacity: '1 !important',
    WebkitTextFillColor: `${k.inputColor} !important`,
    '&::placeholder': {
      color: `${k.placeholderColor} !important`,
      opacity: '1 !important',
      fontWeight: k.inputFontWeight,
      WebkitTextFillColor: `${k.placeholderColor} !important`,
    },
  },
  /**
   * Multiline:
   * - Padding nằm ở OutlinedInput root (cùng 8px/6px như TextInput) — không gắn py lên textarea.
   *   TextareaAutosize đo singleRowHeight rồi × minRows; nếu textarea có padding
   *   thì bị nhân padding → dòng cuối cách viền sai.
   * - Không height/minHeight !important trên textarea (để Autosize set height inline).
   */
  '& .MuiInputBase-input:not(.MuiInputBase-inputMultiline)': {
    height: 'auto !important',
  },
  '& .MuiInputBase-inputSizeSmall:not(.MuiInputBase-inputMultiline)': {
    py: underline ? '6px !important' : '8px !important',
    px: `${k.inputPaddingX}px !important`,
    height: 'auto !important',
  },
  '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
    minHeight: 'unset !important',
    height: 'auto !important',
    alignItems: 'flex-start',
    // Khớp TextInput: py 8 (box) / 6 (underline)
    py: underline ? '6px !important' : '8px !important',
    px: `${k.inputPaddingX}px !important`,
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-input.MuiInputBase-inputMultiline': {
    lineHeight: `${k.lineHeight} !important`,
    py: '0 !important',
    px: '0 !important',
    resize: 'none',
    overflow: 'hidden',
    overflowY: 'hidden',
    boxSizing: 'border-box',
  },
  '& .MuiSelect-select': {
    ...kiotInputFont,
    color: `${k.inputColor} !important`,
    minHeight: `${k.inputHeightPx - 14}px !important`,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    py: '8px !important',
    px: `${k.inputPaddingX}px !important`,
    height: 'auto !important',
    opacity: '1 !important',
  },
  '& .MuiSelect-select.MuiSelect-displayEmpty': {
    color: `${k.placeholderColor} !important`,
    WebkitTextFillColor: `${k.placeholderColor} !important`,
  },
  '& .MuiSelect-select em': {
    fontStyle: 'normal',
    color: `${k.placeholderColor} !important`,
    fontWeight: k.inputFontWeight,
  },
  '& .MuiSelect-icon': {
    color: k.textSecondary,
    right: 10,
    fontSize: '1.35rem',
  },
  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
    minHeight: `${k.inputHeightPx}px !important`,
    height: `${k.inputHeightPx}px !important`,
    maxHeight: `${k.inputHeightPx}px !important`,
    p: '0 !important',
    pt: '0 !important',
    pb: '0 !important',
    pl: `${k.inputPaddingX}px !important`,
    pr: '28px !important',
    flexWrap: 'nowrap !important',
    boxSizing: 'border-box',
    alignItems: 'center',
  },
  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input, & .MuiAutocomplete-inputRoot .MuiInputBase-input':
    {
      ...kiotInputFont,
      color: `${k.inputColor} !important`,
      py: '8px !important',
      px: '0 !important',
      height: 'auto !important',
      minHeight: '0 !important',
      minWidth: '0 !important',
      boxSizing: 'border-box',
      opacity: '1 !important',
      WebkitTextFillColor: `${k.inputColor} !important`,
      '&::placeholder': {
        color: `${k.placeholderColor} !important`,
        opacity: '1 !important',
        fontWeight: k.inputFontWeight,
        WebkitTextFillColor: `${k.placeholderColor} !important`,
      },
    },
  '& .MuiAutocomplete-endAdornment': {
    position: 'absolute',
    top: '50%',
    right: `${Math.max(0, k.inputPaddingX - 2)}px`,
    transform: 'translateY(-50%)',
    height: 'auto',
  },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
    p: '2px',
  },
  '& .MuiFormHelperText-root': {
    fontFamily: k.fontFamily,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: k.lineHeight,
    color: k.textSecondary,
    opacity: 1,
    mx: 0,
    mt: 0.5,
    '&.Mui-error': {
      color: `${theme.palette.error.main} !important`,
    },
  },
  })
}

/** OutlinedInput root khi nằm trong Autocomplete — sx phải gắn slot `input` (sx trên Autocomplete root không emit nested rules). */
export function getFormFieldAutocompleteInputRootSx(surface: FieldSurface = 'form'): SxProps<Theme> {
  const k = getFieldSurfaceStyle(surface)
  const kiotInputFont = buildKiotInputFont(surface)
  const underline = (k.inputChrome ?? 'box') === 'underline'
  const borderWidth = underline ? '0 0 1px 0' : '1px'
  const focusBorderWidth = underline ? '0 0 2px 0' : '1px'
  return (theme) => ({
    minHeight: `${k.inputHeightPx}px !important`,
    height: `${k.inputHeightPx}px !important`,
    maxHeight: `${k.inputHeightPx}px !important`,
    borderRadius: `${k.borderRadiusPx}px !important`,
    bgcolor: `${k.inputBg} !important`,
    p: '0 !important',
    pt: '0 !important',
    pb: '0 !important',
    pl: `${k.inputPaddingX}px !important`,
    pr: '28px !important',
    flexWrap: 'nowrap !important',
    boxSizing: 'border-box',
    alignItems: 'center',
    ...(underline ? { boxShadow: 'none !important' } : {}),
    '&.MuiInputBase-hiddenLabel': {
      pt: '0 !important',
      pb: '0 !important',
    },
    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
      borderRadius: `${k.borderRadiusPx}px !important`,
      borderWidth: `${borderWidth} !important`,
      borderColor: `${k.borderColor} !important`,
      ...(underline
        ? {
            borderStyle: 'solid !important',
            legend: { display: 'none' },
          }
        : {}),
    },
    '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${k.borderHover} !important`,
      ...(underline ? { borderWidth: `${borderWidth} !important` } : {}),
    },
    '&.Mui-focused': {
      boxShadow: `${k.focusShadow} !important`,
    },
    '&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${k.focusColor} !important`,
      borderWidth: `${focusBorderWidth} !important`,
    },
    '&.Mui-error fieldset, &.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.error.main} !important`,
      ...(underline ? { borderWidth: `${borderWidth} !important` } : {}),
    },
    '&.Mui-error:hover fieldset, &.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.error.dark} !important`,
    },
    '&.Mui-error.Mui-focused': {
      boxShadow: underline ? 'none !important' : `0 0 0 2px ${theme.palette.error.main}38 !important`,
    },
    '&.Mui-error.Mui-focused fieldset, &.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.error.main} !important`,
      borderWidth: `${focusBorderWidth} !important`,
    },
    '& .MuiAutocomplete-input, & .MuiInputBase-input': {
      ...kiotInputFont,
      color: `${k.inputColor} !important`,
      py: underline ? '6px !important' : '8px !important',
      px: '0 !important',
      height: 'auto !important',
      minHeight: '0 !important',
      minWidth: '0 !important',
      boxSizing: 'border-box',
      opacity: '1 !important',
      WebkitTextFillColor: `${k.inputColor} !important`,
      '&::placeholder': {
        color: `${k.placeholderColor} !important`,
        opacity: '1 !important',
        fontWeight: k.inputFontWeight,
        WebkitTextFillColor: `${k.placeholderColor} !important`,
      },
    },
    '& .MuiAutocomplete-endAdornment': {
      position: 'absolute',
      top: '50%',
      right: `${Math.max(0, k.inputPaddingX - 2)}px`,
      transform: 'translateY(-50%)',
      height: 'auto',
    },
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
      p: '2px',
    },
  })
}

export const formFieldKiotOutlinedInputSx: SxProps<Theme> = getFormFieldKiotOutlinedInputSx('form')

/** @deprecated alias — dùng `getFormFieldKiotOutlinedInputSx`. */
export const formFieldOutlinedInputSx: SxProps<Theme> = formFieldKiotOutlinedInputSx

/** Nhãn trên viền (layout `outline`). */
export function getFormFieldOutlineLabelSx(surface: FieldSurface = 'form'): SxProps<Theme> {
  const k = getFieldSurfaceStyle(surface)
  return (theme) => ({
    '& .MuiInputLabel-root, & .MuiFormLabel-root': {
      fontFamily: `${k.fontFamily} !important`,
      fontSize: `${k.labelFontSizePx}px !important`,
      fontWeight: `${k.labelFontWeight} !important`,
      color: `${k.labelColor} !important`,
      opacity: '1 !important',
      '& .MuiFormLabel-asterisk, & .MuiInputLabel-asterisk': {
        color: `${theme.palette.error.main} !important`,
      },
    },
    '& .MuiInputLabel-root': {
      '&.MuiInputLabel-shrink': {
        backgroundColor: k.inputBg,
        px: '4px',
        lineHeight: 1.2,
      },
    },
  })
}

export const formFieldOutlineLabelSx: SxProps<Theme> = getFormFieldOutlineLabelSx('form')

/** Vùng bọc form trong modal — nền trắng + font Kiot. */
export const formFieldKiotSurfaceSx: SxProps<Theme> = {
  bgcolor: kForm.surfaceBg,
  fontFamily: kForm.fontFamily,
  fontSize: `${kForm.inputFontSizePx}px`,
  fontWeight: kForm.inputFontWeight,
  color: kForm.inputColor,
}

export function getFormFieldRootSx(surface: FieldSurface = 'form'): SxProps<Theme> {
  const shared = mergeSx(getFormFieldKiotScopeSx(surface), getFormFieldKiotOutlinedInputSx(surface))
  if (isOutlineLabelLayout(surface)) {
    return mergeSx(shared, getFormFieldOutlineLabelSx(surface))
  }
  return shared
}

export function getFormFieldTextFieldSx(surface: FieldSurface = 'form', extra?: SxProps<Theme>): SxProps<Theme> {
  return mergeSx(getFormFieldRootSx(surface), extra)
}

/**
 * Textarea autosize:
 * - minRows=1 → cao + căn giữa giống TextInput
 * - minRows=N → sàn N dòng; ≤N giữ cao; >N tự tăng
 * Padding chỉ ở root (= TextInput 8px) — textarea py/px=0 để Autosize không nhân padding.
 */
export function getFormFieldTextareaSx(
  surface: FieldSurface = 'form',
  extra?: SxProps<Theme>,
  options?: { rows?: number; maxRows?: number },
): SxProps<Theme> {
  const k = getFieldSurfaceStyle(surface)
  const rows = Math.max(1, options?.rows ?? 1)
  const hasMaxRows = options?.maxRows != null && options.maxRows > 0
  const underline = (k.inputChrome ?? 'box') === 'underline'
  const padY = underline ? 6 : 8
  const isSingleRow = rows === 1

  return mergeSx(getFormFieldTextFieldSx(surface), {
    '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
      height: 'auto !important',
      py: `${padY}px !important`,
      px: `${k.inputPaddingX}px !important`,
      boxSizing: 'border-box',
      ...(isSingleRow
        ? {
            minHeight: `${k.inputHeightPx}px !important`,
            alignItems: 'center',
          }
        : {
            minHeight: 'unset !important',
            alignItems: 'flex-start',
          }),
    },
    '& .MuiInputBase-input.MuiInputBase-inputMultiline': {
      py: '0 !important',
      px: '0 !important',
      lineHeight: `${k.lineHeight} !important`,
      boxSizing: 'border-box',
      resize: 'none',
      overflow: hasMaxRows ? 'auto' : 'hidden',
      overflowY: hasMaxRows ? 'auto' : 'hidden',
    },
  }, extra)
}

export function getFormFieldShellSx(surface: FieldSurface = 'form', extra?: SxProps<Theme>): SxProps<Theme> {
  return mergeSx(getFormFieldKiotScopeSx(surface), extra)
}

/** TextInput trong thanh tìm header (ô lọc toolbar). */
export const filterToolbarInlineTextFieldSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  alignSelf: 'stretch',
  '& .MuiOutlinedInput-root': {
    height: '100%',
    minHeight: 'unset',
    borderRadius: 0,
    bgcolor: 'transparent !important',
    boxShadow: 'none !important',
    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '&.Mui-focused': {
      boxShadow: 'none !important',
    },
    '&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
  },
  '& .MuiInputBase-input': {
    py: '0 !important',
    px: '6px !important',
    height: '100% !important',
    minHeight: 'unset !important',
    boxSizing: 'border-box',
    fontSize: '15px !important',
    '&::placeholder': {
      fontSize: '15px !important',
    },
  },
}
